import { createBrowserRouter, redirect, RouterProvider } from 'react-router-dom'
import { fetchMe } from '@/lib/api'
import { loadAppConfig } from '@/lib/query'
import AppLayout from '@/routes/layout'
import ErrorPage from '@/routes/error'
import SignIn from '@/routes/signin'
import Triage from '@/routes/triage'
import Me from '@/routes/me'
import Backlog from '@/routes/backlog'
import TeamArea from '@/routes/team-area'
import TeamPicker from '@/routes/team-picker'
import Sprint from '@/routes/sprint'
import Roadmap from '@/routes/roadmap'
import Health from '@/routes/health'
import Ship from '@/routes/ship'
import Verify from '@/routes/verify'
import MyVerifications from '@/routes/my-verifications'
import Rollouts from '@/routes/rollouts'
import Closed from '@/routes/closed'
import SavedSearch from '@/routes/q'
import IssueDeepLink from '@/routes/issue-deep-link'
import Docs from '@/routes/docs'

const router = createBrowserRouter([
  {
    path: '/signin',
    element: <SignIn />,
    errorElement: <ErrorPage />,
  },
  {
    id: 'app',
    path: '/',
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    // Gate the whole signed-in tree: no session → /signin. Also loads the
    // public app config so queries know which org/repo to talk to.
    loader: async ({ request }) => {
      const [me] = await Promise.all([fetchMe(), loadAppConfig()])
      if (!me) {
        const url = new URL(request.url)
        const attempted = url.pathname + url.search
        return redirect(
          attempted === '/' ? '/signin' : `/signin?returnTo=${encodeURIComponent(attempted)}`,
        )
      }
      return me
    },
    children: [
      { index: true, loader: () => redirect('/me') },
      { path: 'triage', element: <Triage /> },
      { path: 'me', element: <Me /> },
      { path: 'backlog', element: <Backlog /> },
      {
        path: 'team',
        element: <TeamPicker />,
        // Skip the picker once a team is remembered.
        loader: () => {
          const stored = window.localStorage.getItem('n3o-work-last-area')
          return stored ? redirect(`/team/${stored}`) : null
        },
      },
      // No static validation: teams are derived live from area/* labels;
      // an unknown slug renders an empty board rather than a 404.
      { path: 'team/:area', element: <TeamArea /> },
      { path: 'sprint', element: <Sprint /> },
      { path: 'sprint/:id', element: <Sprint /> },
      { path: 'roadmap', element: <Roadmap /> },
      { path: 'health', element: <Health /> },
      // Lazy: apexcharts (~500KB) must not join the main bundle.
      { path: 'insights/:lens?/:entity?', lazy: async () => ({ Component: (await import('@/routes/insights')).default }) },
      { path: 'ship', element: <Ship /> },
      { path: 'verify', element: <Verify /> },
      { path: 'my-verifications', element: <MyVerifications /> },
      { path: 'rollouts', element: <Rollouts /> },
      { path: 'closed', element: <Closed /> },
      { path: 'q', element: <SavedSearch /> },
      { path: 'docs/:doc', element: <Docs /> },
      {
        path: 'issues/:number',
        element: <IssueDeepLink />,
        loader: ({ params }) => {
          const parsed = Number(params.number)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            throw new Response(`bad issue number "${params.number}"`, { status: 404 })
          }
          return null
        },
      },
    ],
  },
])

export default function App() {
  return <RouterProvider router={router} />
}
